import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
	onConnectSocketChat,
	onDisconnectSocketChat as onDisconnectSocket,
	onGetChats,
	onGetMessages,
} from "./ChatThunk";
import { NewMessageResponse } from "../../domain/entities/onNewMessageSocket";
import { UpdatedMessageResponse } from "../../domain/entities/onUpdatedMessageSocket";
import { Chats } from "../../../chat/domain/entities/getChat";
import { ChatMessage } from "../../domain/entities/getMessages";
import { UpdatedChatStatusSocket } from "../../domain/entities/UpdatedChatStatusSocket";

interface ChatState {
	loading: boolean;
	messagesLoading: boolean;
	messageReceived: NewMessageResponse[];
	chats: Chats[];
	selectedChat?: Chats;
	messageHistory: ChatMessage[];
	error?: string;
}

const initialState: ChatState = {
	loading: false,
	messagesLoading: false,
	messageReceived: [],
	chats: [],
	selectedChat: undefined,
	messageHistory: [],
	error: undefined,
};

const chatSlice = createSlice({
	name: "chat",
	initialState,
	reducers: {
		newMessageReceived: (
			state,
			action: PayloadAction<NewMessageResponse>
		) => {
			const chatId = action.payload.message.chat_id;
			const existingMessageIndex = state.messageHistory.findIndex(
				(message) => message.message_id === action.payload.message.message_id
			);
			const chatIndex = state.chats.findIndex(
				(chat) => chat.chat_id === chatId
			);
			const previousChat = chatIndex !== -1 ? state.chats[chatIndex] : undefined;
			let updatedChat;
			if (chatIndex !== -1) {
				updatedChat = {
					...previousChat,
					...action.payload.chat,
					...action.payload.message,
				};
				state.chats.splice(chatIndex, 1);
			} else {
				updatedChat = {
					...action.payload.chat,
					...action.payload.message,
				};
			}
			const previousUnread =
				previousChat ? Number(previousChat.unread_messages || 0) : 0;
			const payloadUnread = Number(action.payload.chat.unread_messages || 0);
			const messageFromOtherUser =
				action.payload.message.sender_id === action.payload.chat.user_id ||
				action.payload.message.message_sender_id === action.payload.chat.user_id;
			if (
				messageFromOtherUser &&
				action.payload.message.read === false &&
				existingMessageIndex === -1
			) {
				updatedChat.unread_messages = Math.max(
					previousUnread + 1,
					payloadUnread,
					1
				);
			}
			state.messageReceived.push(action.payload);
			if (existingMessageIndex !== -1) {
				state.messageHistory[existingMessageIndex] = {
					...state.messageHistory[existingMessageIndex],
					...action.payload.message,
					loading: false,
				};
			} else {
				state.messageHistory.unshift(action.payload.message);
			}
			state.chats.unshift({
				...updatedChat,
				loading: false,
			});
		},
		deleteHistory: (state) => {
			state.messageHistory = [];
		},
		deleteMessageText: (state, action: PayloadAction<{chat_id: string}>) => {
			const chatIndex = state.chats.findIndex((chat) => chat.chat_id === action.payload.chat_id);
			state.chats[chatIndex].message_text = "";
		},
		deleteChat: (state, action: PayloadAction<{ chat_id: string }>) => {
			const chatIndex = state.chats.findIndex((chat) => chat.chat_id === action.payload.chat_id);
			if (chatIndex !== -1) state.chats.splice(chatIndex, 1);
		},
		resetUnreadMessages: (
			state,
			action: PayloadAction<{ chat_id: string }>
		) => {
			const chat = state.chats.find(
				(chat) => chat.chat_id === action.payload.chat_id
			);
			if (chat) chat.unread_messages = 0;
		},
		messageUpdatedRead: (
			state,
			action: PayloadAction<UpdatedMessageResponse[]>
		) => {
			action.payload.forEach((updatedMessage) => {
				const message = state.messageHistory.find(
					(msg) =>
						msg.chat_id === updatedMessage.chat_id &&
						msg.message_id === updatedMessage.message_id
				);
				if (message) message.read = updatedMessage.read;
				
				const chat = state.chats.find(
					(ch) =>
						ch.chat_id === updatedMessage.chat_id &&
						ch.message_id === updatedMessage.message_id
				);
				if (chat) chat.read = updatedMessage.read;
			});
		},
		tempMessageReceived: (
			state,
			action: PayloadAction<ChatMessage>
		) => {
			const { message_id, message_text } = action.payload;
			state.messageHistory = state.messageHistory.map((message) => {
				if (message.message_id === message_id) {
					return {
						...message,
						message_text, 
					};
				}
				return message; 
			});
		},
		updatedChatStatusReceived: (state, action: PayloadAction<UpdatedChatStatusSocket>) => {
			const updatedStatus = action.payload;
			const chat = state.chats.find((chat) => chat.chat_id === updatedStatus.id);
			if (chat) {
				const isUser1 = updatedStatus.user_id1 === chat.user_id;
				const newStatusUser1 = isUser1 ? updatedStatus.status_user_id2 : updatedStatus.status_user_id1;
				const newStatusUser2 = isUser1 ? updatedStatus.status_user_id1 : updatedStatus.status_user_id2;
				chat.status_user_id1 = newStatusUser2;
				chat.status_user_id2 = newStatusUser1;
				if (state.selectedChat && state.selectedChat.chat_id === updatedStatus.id) {
					state.selectedChat.status_user_id1 = newStatusUser2;
					state.selectedChat.status_user_id2 = newStatusUser1;
				}
			}
		},
		setSelectedChat: (state, action: PayloadAction<Chats>) => {
			state.selectedChat = action.payload;
		},
		deleteSelectedChat: (state) => {
			state.selectedChat = undefined;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(onGetChats.pending, (state) => {
				state.messagesLoading = true;
			})
			.addCase(
				onGetChats.fulfilled,
				(state, action: PayloadAction<Chats[]>) => {
					state.messagesLoading = false;
					state.chats = action.payload;
				}
			)
			.addCase(onGetChats.rejected, (state, action) => {
				state.error = action.error.message;
				state.messagesLoading = false;
			})
			.addCase(onGetMessages.pending, (state) => {
				state.messagesLoading = true;
			})
			.addCase(
				onGetMessages.fulfilled,
				(state, action: PayloadAction<ChatMessage[]>) => {
					state.messagesLoading = false;
					action.payload.forEach((incomingMessage) => {
						const existingMessageIndex = state.messageHistory.findIndex(
							(message) => message.message_id === incomingMessage.message_id
						);

						if (existingMessageIndex !== -1) {
							state.messageHistory[existingMessageIndex] = {
								...state.messageHistory[existingMessageIndex],
								...incomingMessage,
							};
							return;
						}

						state.messageHistory.push(incomingMessage);
					});
				}
			)
			.addCase(onGetMessages.rejected, (state, action) => {
				state.error = action.error.message;
				state.messagesLoading = false;
			})
			.addCase(onConnectSocketChat.pending, (state) => {
				state.loading = true;
			})
			.addCase(onConnectSocketChat.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(onConnectSocketChat.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			.addCase(onDisconnectSocket.pending, (state) => {
				state.loading = true;
			})
			.addCase(onDisconnectSocket.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(onDisconnectSocket.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
	},
});

export const {
	newMessageReceived,
	messageUpdatedRead,
	tempMessageReceived,
	deleteHistory,
	resetUnreadMessages,
	deleteSelectedChat,
	setSelectedChat,
	updatedChatStatusReceived,
	deleteChat,
	deleteMessageText,
} = chatSlice.actions;
export default chatSlice.reducer;
